import React from "react";
import Tooltip from "@mui/material/Tooltip";
import { TextControlStyle } from "../../Utilities/FormDesignConstants";
import { StyledFormHelperText, StyledTextField } from "./CommonStyled";
import { IssueData } from "../../Utilities/ControlRenderContext";
import { TextEntryFormControl } from "../TextEntryFormControl";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { NumberDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/NumberDataDefinition";

import { PropertyEditorInteger } from "../../../CommonUI/PropertyEditing/PropertyEditorInteger";

import { ValidationIssue } from "../../../rule-engine-core/Common/IDesignValidationProvider";
import { PropertyEditorText } from "../../../CommonUI/PropertyEditing/PropertyEditorText";
import { InputAdornment } from "@mui/material";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

class XCurrencyFieldEditUI extends React.Component<{ editMe: CurrencyFormControl }> {
    render() {
        return (
            <UISpacedBoxContent>
                {this.props.editMe.renderBaseDataControlEditor()}

                <PropertyEditorText
                    editObject={this.props.editMe}
                    propertyName={"currencyType"}
                    label="Currency Type"
                />

                <PropertyEditorInteger
                    editObject={this.props.editMe}
                    propertyName={"min"}
                    label="Min"
                    decimal
                />

                <PropertyEditorInteger
                    editObject={this.props.editMe}
                    propertyName={"max"}
                    label="Max"
                    decimal
                />

                {this.props.editMe.renderTextControlBasePropertyEditor()}
                {this.props.editMe.renderTextStyleSelectionEditor()}
            </UISpacedBoxContent>
        );
    }
}

export class CurrencyFormControl extends TextEntryFormControl {
    readonly __type: string = "Currency";
    min?: number;
    max?: number;
    currencyType: string = "$";

    render() {
        let style = this.getFormContext()?.form?.defaultTextStyle;

        if (this.overrideStyle && this.style) style = this.style;

        let size: "medium" | "small" = this.getFormContext()?.form?.defaultTextSize ?? "medium";

        if (this.overrideStyle && this.size) size = this.size;

        const issueData: IssueData | null = this.getFormContext()!.getControlContext(this)!.getRuntimeIssueData();
        const issueText: string = issueData?.text || "";
        const customWidth = this.fullWidth ? "100%" : this.width ? `${this.width}px` : "200px";

        switch (style) {
            case TextControlStyle.LABELED:
                return (
                    <>
                        {this.label && <UILabel variant={"form_label"}>{this.label}</UILabel>}
                        <StyledTextField
                            size={size}
                            data-size={size}
                            type={"number"}
                            variant={"outlined"}
                            value={this.getValue()}
                            onChange={this.handleChange}
                            data-testid={this.valueName}
                            fullWidth={this.fullWidth}
                            width={customWidth}
                            error={Boolean(issueText)}
                            InputProps={{
                                inputProps: {
                                    min: this.min,
                                    max: this.max,
                                    step: 0.01,
                                },
                                startAdornment: <InputAdornment position="start">{this.currencyType}</InputAdornment>,
                                inputRef: this.ref,
                            }}
                        />
                        {issueText && issueText.length > 30 && (
                            <Tooltip
                                title={issueText}
                                arrow
                                placement="bottom"
                            >
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "numberbox"}-hinttext`}
                                >
                                    {issueText.slice(0, 30) + "..."}
                                </StyledFormHelperText>
                            </Tooltip>
                        )}
                        {((issueText && issueText.length < 31) || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "numberbox"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </>
                );
            case TextControlStyle.SHADED:
                return (
                    <>
                        <StyledTextField
                            size={size}
                            data-size={size}
                            type={"number"}
                            variant={"filled"}
                            customstyle={"filled"}
                            value={this.getValue()}
                            onChange={this.handleChange}
                            data-testid={this.valueName}
                            fullWidth={this.fullWidth}
                            width={customWidth}
                            error={Boolean(issueText)}
                            label={this.valueName}
                            InputProps={{
                                inputProps: {
                                    min: this.min,
                                    max: this.max,
                                    step: 0.01,
                                },
                                startAdornment: <InputAdornment position="start">{this.currencyType}</InputAdornment>,
                                inputRef: this.ref,
                            }}
                        />
                        {issueText && issueText.length > 30 && (
                            <Tooltip
                                title={issueText}
                                arrow
                                placement="bottom"
                            >
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "numberbox"}-hinttext`}
                                >
                                    {issueText.slice(0, 30) + "..."}
                                </StyledFormHelperText>
                            </Tooltip>
                        )}
                        {((issueText && issueText.length < 31) || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "numberbox"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </>
                );
            case TextControlStyle.UNDERLINED:
                return (
                    <>
                        <StyledTextField
                            size={size}
                            data-size={size}
                            type={"number"}
                            variant={"standard"}
                            customstyle={"standard"}
                            value={this.getValue()}
                            onChange={this.handleChange}
                            data-testid={this.valueName}
                            fullWidth={this.fullWidth}
                            width={customWidth}
                            error={Boolean(issueText)}
                            label={this.valueName}
                            InputProps={{
                                inputProps: {
                                    min: this.min,
                                    max: this.max,
                                    step: 0.01,
                                },
                                startAdornment: <InputAdornment position="start">{this.currencyType}</InputAdornment>,
                                inputRef: this.ref,
                            }}
                        />
                        {issueText && issueText.length > 30 && (
                            <Tooltip
                                title={issueText}
                                arrow
                                placement="bottom"
                            >
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "numberbox"}-hinttext`}
                                >
                                    {issueText.slice(0, 30) + "..."}
                                </StyledFormHelperText>
                            </Tooltip>
                        )}
                        {((issueText && issueText.length < 31) || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "numberbox"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </>
                );
            case TextControlStyle.OUTLINE:
                return (
                    <>
                        <StyledTextField
                            size={size}
                            data-size={size}
                            type={"number"}
                            variant={"outlined"}
                            customstyle={"outlined"}
                            value={this.getValue()}
                            onChange={this.handleChange}
                            data-testid={this.valueName}
                            fullWidth={this.fullWidth}
                            width={customWidth}
                            error={Boolean(issueText)}
                            label={this.valueName}
                            InputProps={{
                                inputProps: {
                                    min: this.min,
                                    max: this.max,
                                    step: 0.01,
                                },
                                startAdornment: <InputAdornment position="start">{this.currencyType}</InputAdornment>,
                                inputRef: this.ref,
                            }}
                        />
                        {issueText && issueText.length > 30 && (
                            <Tooltip
                                title={issueText}
                                arrow
                                placement="bottom"
                            >
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "numberbox"}-hinttext`}
                                >
                                    {issueText.slice(0, 30) + "..."}
                                </StyledFormHelperText>
                            </Tooltip>
                        )}
                        {((issueText && issueText.length < 31) || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "numberbox"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </>
                );
        }
    }

    getRuntimeValidationIssues(): ValidationIssue[] {
        const runtimeIssues = super.getRuntimeValidationIssues();
        const validationIssue = this.validate();
        if (validationIssue) {
            runtimeIssues.push(new ValidationIssue(validationIssue, this.valueName, this.id));
        }

        return runtimeIssues;
    }

    renderEditUI(): JSX.Element | null {
        return <XCurrencyFieldEditUI editMe={this} />;
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.valueName) return;

        let def = new NumberDataDefinition();
        def.name = this.valueName;
        return def;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(this, retData, "__type", "min", "max", "currencyType");

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(this, data, "__type", "min", "max", "currencyType");
    }

    private handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (this.valueName) {
            let value = event.currentTarget.value;
            this.setFormDataValue(this.valueName, value);
        }
    };

    private getValue = () => {
        return this.valueName ? this.getFormDataValue(this.valueName) : "";
    };

    private validate = () => {
        if (!this.min && !this.max) return null;
        let value = this.valueName ? this.getFormDataValue(this.valueName) : "";
        if (this.min && parseFloat(value) < this.min) return `Value should be larger than ${this.min}`;
        if (this.max && parseFloat(value) > this.max) return `Value should be smaller than ${this.max}`;
    };
}
